import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AuthRole, AuthUser, LoginResult } from "./authApi";

export type AuthStatus = "idle" | "submitting" | "authenticated" | "error";

const RANK: Record<AuthRole, number> = { viewer: 0, operator: 1, admin: 2 };

interface AuthState {
	token: string | null;
	user: AuthUser | null;
	/** Unix seconds the session token expires. */
	expiresAt: number | null;
	status: AuthStatus;
	error: string | null;

	setStatus: (status: AuthStatus) => void;
	setError: (error: string | null) => void;
	/** Store a verified session (the result of /auth/login). */
	setSession: (result: LoginResult) => void;
	/** Clear the session token. */
	logout: () => void;
}

/**
 * Email/password session store. Holds the verified session (JWT + the
 * public user shape, including role). The token is persisted to
 * localStorage so a reload keeps the operator signed in until it
 * expires. The login flow lives in `useEmailLogin`, which drives this
 * store via `setStatus`/`setSession`.
 */
export const useAuthStore = create<AuthState>()(
	persist(
		(set) => ({
			token: null,
			user: null,
			expiresAt: null,
			status: "idle",
			error: null,

			setStatus: (status) => set({ status }),
			setError: (error) => set({ error }),
			setSession: (result) =>
				set({
					token: result.token,
					user: result.user,
					expiresAt: result.expiresAt,
					status: "authenticated",
					error: null,
				}),
			logout: () =>
				set({
					token: null,
					user: null,
					expiresAt: null,
					status: "idle",
					error: null,
				}),
		}),
		{
			name: "kam-auth",
			// Only persist the session itself — never transient UI state.
			partialize: (s) => ({
				token: s.token,
				user: s.user,
				expiresAt: s.expiresAt,
			}),
		},
	),
);

/**
 * Non-React accessor for the live, unexpired token (used by the API
 * client to attach the bearer header). Clears the session if expired.
 */
export function getAuthToken(): string | null {
	const { token, expiresAt } = useAuthStore.getState();
	if (!token) return null;
	if (expiresAt && Date.now() / 1000 >= expiresAt) {
		useAuthStore.getState().logout();
		return null;
	}
	return token;
}

/** True when there's a non-expired session. */
export function isAuthenticated(): boolean {
	return getAuthToken() !== null;
}

/** The signed-in user's role, or null when there's no live session. */
export function getRole(): AuthRole | null {
	if (!isAuthenticated()) return null;
	return useAuthStore.getState().user?.role ?? null;
}

/** True when the signed-in user's role ranks at least `min`. */
export function hasRole(min: AuthRole): boolean {
	const role = getRole();
	if (!role) return false;
	return RANK[role] >= RANK[min];
}

export function explainAuthError(err: unknown): string {
	const msg = err instanceof Error ? err.message : String(err);
	// `/auth/login` returns a coarse 401 by design — present a single,
	// non-leaky message for any rejection.
	if (/401/.test(msg)) {
		return "Invalid email or password.";
	}
	return msg;
}
